import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate';
import { useAppSelector } from '../../store/hooks';
import {
    Select,
    Button,
    TextInput,
    CustomDatePicker,
    RadioButton,
    GenericError,
    PageLoader,
} from '../common';
import { useEditEvent } from './useEditEvent';
import { eventTypeOptions } from '../../utils/consts';
import { useParams } from 'react-router-dom';
import { useTranslation } from '../../hooks/translations/TranslationProvider';

import './editEvent.css';

const EditEvent: React.FC = () => {
    const { eventId } = useParams();
    const navigate = useLocalizedNavigate();
    const { t } = useTranslation();

    const {
        formValues,
        formErrors,
        inputChangeHandler,
        onEventTypeChange,
        onDateChange,
        onRadioChange,
        onSave,
        isLoading,
        isSaveLoading,
        error,
    } = useEditEvent(eventId);
    const auth = useAppSelector(state => state.auth);

    const onGoBack = () => {
        navigate(-1);
    };

    const onSaveHandler = (event: React.FormEvent) => {
        event.preventDefault();

        onSave();
    };

    if (isLoading && !error && !formValues.name) {
        return <PageLoader />;
    }

    if (error) {
        return <GenericError error={error} />;
    }

    return (
        <div className="edit-event">
            <div className="edit-event-form-wrapper">
                <form onSubmit={onSaveHandler} className="edit-event-form">
                    <RadioButton
                        label="Event type"
                        checked={formValues.isPrivate}
                        onChange={onRadioChange}
                        options={[
                            {
                                name: 'public',
                                title: t('createEvent.open'),
                                tooltip: t('createEvent.publicEvent'),
                            },
                            {
                                name: 'private',
                                title: t('createEvent.approvalRequired'),
                                tooltip: t('createEvent.privateEvent'),
                            },
                        ]}
                    />
                    <TextInput
                        name="name"
                        placeholder="Event name"
                        label={t('createEvent.name')}
                        onChange={inputChangeHandler}
                        value={formValues.name}
                        error={formErrors.name}
                        className="full-width"
                    />
                    <Select
                        name="type"
                        options={eventTypeOptions.map(it => ({
                            ...it,
                            label: t(`eventTypes.${it.label.toLowerCase()}`),
                        }))}
                        onSelect={onEventTypeChange}
                        value={formValues.type as string}
                        label={t('createEvent.type')}
                        className="full-width"
                        error={formErrors.type}
                    />
                    <TextInput
                        value={formValues.maxUsers}
                        name="maxUsers"
                        label={t('createEvent.totalNumber')}
                        onChange={inputChangeHandler}
                        className="full-width"
                        error={formErrors.maxUsers}
                    />
                    <TextInput
                        value={auth.authData?.city.name || ''}
                        name="city"
                        label={t('common.city')}
                        isDisabled
                        onChange={() => null}
                        className="full-width"
                    />
                    <TextInput
                        value={formValues.place}
                        name="place"
                        label={t('createEvent.street')}
                        placeholder={t('createEvent.street')}
                        onChange={inputChangeHandler}
                        error={formErrors.place}
                        className="full-width"
                    />
                    <TextInput
                        value={formValues.address}
                        name="address"
                        label={t('createEvent.street')}
                        placeholder={t('createEvent.street')}
                        onChange={inputChangeHandler}
                        className="full-width"
                    />
                    <TextInput
                        value={formValues.mapsUrl}
                        name="mapsUrl"
                        label={t('createEvent.mapsLink')}
                        placeholder={t('createEvent.mapsLinkPlaceholder')}
                        onChange={inputChangeHandler}
                        className="full-width"
                    />
                    <CustomDatePicker
                        name="date"
                        selected={formValues.date}
                        onChange={date => onDateChange(date!)}
                        minDate={new Date()}
                        label={t('createEvent.dateAndTime')}
                        dateFormat="dd/MM/yyyy HH:mm"
                        showTimeSelect
                        className="full-width"
                        error={formErrors.date}
                    />
                    <TextInput
                        value={formValues.duration}
                        name="duration"
                        label={t('createEvent.duration')}
                        onChange={inputChangeHandler}
                        className="full-width"
                        error={formErrors.duration}
                    />
                    <div className="double-input-wrapper">
                        <TextInput
                            value={formValues.totalPrice}
                            name="totalPrice"
                            label={t('createEvent.totalPrice')}
                            placeholder={t('createEvent.totalPricePlaceholder')}
                            onChange={inputChangeHandler}
                            error={formErrors.totalPrice}
                        />
                        <TextInput
                            value={auth.authData?.currency || ''}
                            name="currency"
                            label={t('createEvent.currency')}
                            isDisabled
                            onChange={() => null}
                        />
                    </div>
                    <TextInput
                        value={formValues.ownerNotes}
                        name="ownerNotes"
                        label={t('createEvent.additionalNotes')}
                        placeholder={t(
                            'createEvent.additionalNotesPlaceholder'
                        )}
                        onChange={inputChangeHandler}
                        className="full-width"
                        isTextArea
                    />
                    <div className="edit-event-buttons">
                        <Button
                            type="button"
                            text={t('common.cancel')}
                            onClick={onGoBack}
                            size="lg"
                        />
                        <Button
                            type="submit"
                            text={t('common.save')}
                            variant="green"
                            size="lg"
                            disabled={isSaveLoading}
                        />
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditEvent;
